import { DialogLayout } from '@/ui/composites/DialogLayout';
import { dialogRhythm } from '@/ui/composites/dialogRhythm';
import { wizardDialogTitleSlotSx } from '@/ui/composites/WizardDialog.styles';
import { Inline } from '@/ui/layout/Inline';
import { Button, IconButton, Text } from '@/ui/primitives';
import { CloseIcon } from '@/ui/primitives/icons';
import type { ReactNode } from 'react';

type WizardDialogProps = {
  open: boolean;
  title: string;
  description: ReactNode;
  currentStepIndex: number;
  totalSteps: number;
  children: ReactNode;
  skipLabel?: string;
  backLabel?: string;
  nextLabel?: string;
  finishLabel?: string;
  closeAriaLabel?: string;
  onClose: () => void;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
};

type WizardFooterActionsProps = Pick<
  WizardDialogProps,
  'skipLabel' | 'nextLabel' | 'finishLabel' | 'onSkip' | 'onNext' | 'onFinish'
> & {
  isLastStep: boolean;
};

const WizardFooterActions = ({
  skipLabel,
  nextLabel,
  finishLabel,
  isLastStep,
  onSkip,
  onNext,
  onFinish,
}: WizardFooterActionsProps): ReactNode => (
  <>
    <Button variant="ghost" fullWidthOnMobile={false} onClick={onSkip}>
      {skipLabel}
    </Button>
    <Button
      fullWidthOnMobile={false}
      onClick={() => {
        if (isLastStep) {
          onFinish();
          return;
        }
        onNext();
      }}
    >
      {isLastStep ? finishLabel : nextLabel}
    </Button>
  </>
);

export const WizardDialog = ({
  open,
  title,
  description,
  currentStepIndex,
  totalSteps,
  children,
  skipLabel = 'Skip',
  backLabel = 'Back',
  nextLabel = 'Next',
  finishLabel = 'Finish',
  closeAriaLabel = 'Close wizard',
  onClose,
  onSkip,
  onBack,
  onNext,
  onFinish,
}: WizardDialogProps): ReactNode => {
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const footerStartSlot = isFirstStep ? undefined : (
    <Button variant="secondary" fullWidthOnMobile={false} onClick={onBack}>
      {backLabel}
    </Button>
  );

  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      titleSlot={
        <Inline
          gap={dialogRhythm.actionsGap}
          align="center"
          wrap="nowrap"
          sx={wizardDialogTitleSlotSx}
        >
          <Text role="bodyMuted">{`Step ${String(currentStepIndex + 1)} of ${String(
            totalSteps,
          )}`}</Text>
          <IconButton aria-label={closeAriaLabel} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Inline>
      }
      footerStartSlot={footerStartSlot}
      actionsGap={dialogRhythm.actionsGap}
      actions={
        <WizardFooterActions
          skipLabel={skipLabel}
          nextLabel={nextLabel}
          finishLabel={finishLabel}
          isLastStep={isLastStep}
          onSkip={onSkip}
          onNext={onNext}
          onFinish={onFinish}
        />
      }
    >
      {children}
    </DialogLayout>
  );
};
